import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PostLoginRedirect } from "@/components/auth/PostLoginRedirect";
import { VersionBadge } from "@/components/layout/VersionBadge";

// Public pages
import Index from "./pages/Index";
import Legal from "./pages/Legal";
import Privacy from "./pages/Privacy";
import WhoWeAre from "./pages/WhoWeAre";

// Auth pages
import Login from "./pages/auth/Login";
import ResetPassword from "./pages/auth/ResetPassword";
import SetupAccount from "./pages/auth/SetupAccount";
import SignIn from "./pages/auth/SignIn";
import AuthCallback from "./pages/auth/Callback";
import NoInvite from "./pages/auth/NoInvite";
import Claim from "./pages/auth/Claim";

// Misc
import NotFound from "./pages/NotFound";

// Pulse (v2) employee surface
import { EmployeeLayout } from "@/components/employee/EmployeeLayout";
import EmployeeHome from "./pages/app/EmployeeHome";
import Welcome from "./pages/app/Welcome";
import Trust from "./pages/app/Trust";
import MyData from "./pages/app/MyData";
import Charles from "./pages/app/Charles";
import SurveyTake from "./pages/app/SurveyTake";

// Pulse (v2) admin surface
import { AdminLayout } from "@/components/admin/AdminLayout";
import OrgOverview from "./pages/app/admin/OrgOverview";
import AdminTrends from "./pages/app/admin/Trends";
import AdminInsights from "./pages/app/admin/Insights";
import AdminTrust from "./pages/app/admin/AdminTrust";
import Gauntlet from "./pages/app/admin/Gauntlet";
import AdminPeople from "./pages/app/admin/People";
import AdminSurveysList from "./pages/app/admin/surveys/SurveysList";
import AdminNewSurvey from "./pages/app/admin/surveys/NewSurvey";
import AdminSurveyEditor from "./pages/app/admin/surveys/SurveyEditor";
import AdminSurveyReport from "./pages/app/admin/surveys/SurveyReport";
import AdminSurveyAnalytics from "./pages/app/admin/surveys/SurveyAnalytics";
import AdminInsightsThemes from "./pages/app/admin/insights/Themes";
import AdminEmailTest from "./pages/app/admin/dev/EmailTest";

// Platform console
import { PlatformGuard } from "./pages/platform/PlatformGuard";
import { PlatformLayout } from "./pages/platform/PlatformLayout";
import Tenants from "./pages/platform/Tenants";

import { DevHarnessGuard, TalkDebugLazy } from "@/components/auth/DevHarnessGuard";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
      staleTime: 1000 * 60,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        {import.meta.env.DEV && <VersionBadge />}
        <AuthProvider>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Index />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/who-we-are" element={<WhoWeAre />} />

            {/* Auth */}
            <Route path="/login" element={<Login />} />
            <Route path="/auth/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            <Route path="/auth/setup-account" element={<SetupAccount />} />
            <Route path="/auth/signin" element={<SignIn />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/no-invite" element={<NoInvite />} />
            <Route path="/auth/claim" element={<Claim />} />

            {/* Post-login dispatcher */}
            <Route path="/redirect" element={<ProtectedRoute><PostLoginRedirect /></ProtectedRoute>} />

            {/* Pulse employee surface */}
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <EmployeeLayout />
                </ProtectedRoute>
              }
            >
              <Route path="employee" element={<EmployeeHome />} />
              <Route path="employee/charles" element={<Charles />} />
              <Route path="employee/survey" element={<SurveyTake />} />
              <Route path="me" element={<MyData />} />
              <Route path="talk" element={<Navigate to="/app/employee/charles" replace />} />
              <Route path="trust" element={<Trust />} />
            </Route>

            <Route path="/app/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />

            {/* Pulse admin surface */}
            <Route path="/app/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route index element={<OrgOverview />} />
              <Route path="people" element={<AdminPeople />} />
              <Route path="trends" element={<AdminTrends />} />
              <Route path="surveys" element={<AdminSurveysList />} />
              <Route path="surveys/new" element={<AdminNewSurvey />} />
              <Route path="surveys/:surveyId" element={<AdminSurveyEditor />} />
              <Route path="surveys/:surveyId/report" element={<AdminSurveyReport />} />
              <Route path="surveys/:surveyId/analytics" element={<AdminSurveyAnalytics />} />
              <Route path="insights" element={<AdminInsights />} />
              <Route path="insights/themes" element={<AdminInsightsThemes />} />
              <Route path="trust" element={<AdminTrust />} />
              <Route path="gauntlet" element={<PlatformGuard><Gauntlet /></PlatformGuard>} />
              <Route path="dev/email-test" element={<AdminEmailTest />} />
            </Route>

            {/* Platform console (platform admins only) */}
            <Route path="/app/platform" element={<PlatformGuard><PlatformLayout /></PlatformGuard>}>
              <Route index element={<Navigate to="tenants" replace />} />
              <Route path="tenants" element={<Tenants />} />
            </Route>

            <Route
              path="/app/talk/_debug"
              element={
                <ProtectedRoute>
                  <DevHarnessGuard>
                    <TalkDebugLazy />
                  </DevHarnessGuard>
                </ProtectedRoute>
              }
            />

            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
